import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { debounce } from 'throttle-debounce';

import { openEventChannel } from '../../../actions/socket';
import { selectShowErrorPage } from '../../../selectors/errorPageSelector';
import { useAppDispatch } from '../../../types/redux/state';
import { ESocketCloseCustomCode } from '../../../types/socket';
import { useApplicationContext } from '../../../utils/applicationContext';

const SOCKET_CLOSE_TIMEOUT = 30000;

/**
 * @description
 * Изменение сокет-соединения при переключении между вкладками / окнами
 */
export const useFocusChange = () => {
  const socketDisconnectTimer = useRef<NodeJS.Timeout | null>(null);
  const showErrorPage = useSelector(selectShowErrorPage);
  const dispatch = useAppDispatch();
  const {
    custom: { socket },
    config,
  } = useApplicationContext();
  const socketCloseTimeout = config.get<number>('frontend-chats.socketCloseTimeout') || SOCKET_CLOSE_TIMEOUT;

  /**
   * Делаем debounce, поскольку функция может срабатывать одновременно и в visiblitychange, и в blur.
   */
  const startSocketDisconnectTimer = useCallback(
    debounce(300, () => {
      socketDisconnectTimer.current = setTimeout(async () => {
        const isConnectionClosed = socket?.eventChannel.isChannelClosed();

        if (socket && !isConnectionClosed) {
          await socket.eventChannel.closeEventChannel(ESocketCloseCustomCode.CurrentBrowserTabBecomeInactive);
        }
      }, socketCloseTimeout);
    }),
    [socket],
  );

  /**
   * @description
   * В Safari при переключении на вкладку фокус вызывается 2 раза, фиксим это дебаунсом.
   */
  const trySocketReconnectIfNeeded = useCallback(
    debounce(300, () => {
      if (socketDisconnectTimer.current) {
        clearTimeout(socketDisconnectTimer.current);
        socketDisconnectTimer.current = null;
      }

      const isConnectionClosed = socket?.eventChannel.isChannelClosed();

      if (!showErrorPage && isConnectionClosed) {
        dispatch(openEventChannel(true));
      }
    }),
    [showErrorPage, socket],
  );

  /**
   * @description
   * Переподключаемся к сокету при активации вкладки.
   * document.addEventListener('visibilitychange') не подходит, поскольку фиксирует видимость вкладки.
   * Если пользователь работает в двух окнах параллельно, то видимая вкладка !== активная
   */
  useEffect(() => {
    window.addEventListener('focus', trySocketReconnectIfNeeded);

    return () => {
      window.removeEventListener('focus', trySocketReconnectIfNeeded);
    };
  }, [trySocketReconnectIfNeeded]);

  /**
   * @description
   * При активации другого окна / другой вкладки стартуем таймер отключения от сокета.
   */
  useEffect(() => {
    window.addEventListener('blur', startSocketDisconnectTimer);

    return () => {
      window.removeEventListener('blur', startSocketDisconnectTimer);
    };
  }, [startSocketDisconnectTimer]);

  /**
   * @description
   * При переходе на другую вкладку с текущей стартуем таймер отключения от сокета.
   * При возвращении на текущую вкладку пробуем переподключиться к сокету, если соединение было прервано.
   */
  useEffect(() => {
    const visibilityChangeHandler = () => {
      if (document.visibilityState === 'hidden') {
        startSocketDisconnectTimer();
      } else {
        trySocketReconnectIfNeeded();
      }
    };

    document.addEventListener('visibilitychange', visibilityChangeHandler);

    return () => {
      document.removeEventListener('visibilitychange', visibilityChangeHandler);
    };
  });
};
